import { Translations } from './types';

export const en: Translations = {
  navbar: {
    home: 'Home',
    projects: 'Projects',
  },
  home: {
    bio: (years) => [
      `Results-driven Senior Software Engineer with over ${years} years of experience building scalable, maintainable web applications using React, Next.js, TypeScript, and modern JavaScript ecosystems. Proven track record of delivering high-quality solutions across fintech, SaaS, travel, and education sectors, with expertise in full-stack development, system architecture, and technical leadership. Passionate about clean code, performance optimization, and modern development practices.`,
    ],
    aboutTitle: 'About',
    aboutTitleAccent: 'Me',
    about1: [
      'I take great pride in my commitment to software craftsmanship, always striving to produce code that is not only clean and efficient but also adheres to the principles of staying DRY and consistent design patterns.',
      'With a background in IT Technical Analysis, my approach to problem-solving is rooted in pragmatism. I believe in addressing challenges with a keen understanding of real-world constraints, ensuring that the solutions I craft are not just theoretically sound but also practical and aligned with project requirements.',
    ],
    experienceTitle: ['Brands', 'worked with'],
  },
  aboutMe: {
    getInTouch: 'GET IN TOUCH',
    messageSent: 'MESSAGE SENT',
    modalTitle: 'SEND ME A MESSAGE',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    subject: 'Subject',
    cancel: 'Cancel',
    submit: 'Submit',
    errors: {
      firstName: 'Please enter your first name',
      lastName: 'Please enter your last name',
      email: 'Please enter your email',
      emailInvalid: 'Please enter a valid email address',
      subject: 'Please enter a message',
    },
  },
  projects: {
    bio: [
      'Personal projects that I have worked on for the past year or more.',
      'Please feel free to check out the github links for the listed projects.',
    ],
    reposTotal: 'Github Public Repos Total:',
    loading: 'Loading projects...',
    repositories: 'Repositories',
    sortBy: 'Sort by:',
    newest: 'Newest',
    oldest: 'Oldest',
    sectionTitle: 'Projects',
    sectionTitleAccent: 'Open source',
    noDescription: 'No description available.',
    created: 'Created:',
    githubButton: 'GitHub ↗',
  },
  companies: {
    orchahealth: {
      title: 'Orcha Health',
      intro:
        'Senior Software Engineer working across the full stack on a digital health platform, delivering frontend architecture improvements, secure API design, and end-to-end CI/CD pipelines using Next.js, TypeScript, C#/.NET, Cypress, Storybook, MUI, SCSS, Docker, and Azure DevOps.',
      highlights: [
        {
          title: 'Component Library Architecture',
          body: 'Decomposed a monolithic UI component library into six independently versioned packages, a shared Core UI library of primitive components and five domain-specific libraries (Form UI, Widget UI, and others) — following a unidirectional dependency pattern. Improved frontend performance by 30% and eliminated cross-library coupling that had been blocking independent releases.',
        },
        {
          title: 'White-Label Supplier Hub',
          body: 'Designed and built a white-label web application in Next.js/React/TypeScript featuring JWT authentication, Role-Based Access Control (RBAC), server-side data fetching, per-supplier UI theming (colour schemes and logos), and in-app health assessment product access.',
        },
        {
          title: 'API Security & Performance',
          body: 'Implemented secure .NET API endpoints with rate limiting, JWT validation, and granular permission checks. Replaced ad-hoc query logic with optimised stored procedures and well-defined DTOs, eliminating redundant SQL execution that had been causing intermittent timeouts under high traffic — improving API stability and reducing unnecessary infrastructure costs.',
        },
        {
          title: 'Security Remediation',
          body: 'Proactively assessed all frontend applications for exposure to the Axios 1.14.1 vulnerability, mitigating risk across legacy and modern frontends. Remediated critical vulnerabilities across legacy Next.js 8 codebases including HTTP header misconfigurations, npm supply chain exposure, and a React Remote Code Execution (RCE) flaw, alongside a full dependency audit and upgrade.',
        },
        {
          title: 'CI/CD & Release Ownership',
          body: 'Owned end-to-end frontend release processes across all consumer applications, authoring Azure DevOps pipelines from scratch — including multi-stage YAML build configurations, Dockerfiles, Helm chart templating, and environment-specific variable group management — delivering fast, repeatable, and environment-isolated deployments across dev, staging, and production.',
        },
      ],
    },
    natwest: {
      title: 'NatWest Rooster Money',
      intro:
        'Contributed to the full UI rebrand following the NatWest acquisition, implementing new features and resolving bugs across the React Cordova codebase, aligning the Rooster Money app with NatWest\'s design language and brand standards using React, TypeScript, Redux Toolkit, SCSS, and Framer Motion.',
      highlights: [
        {
          title: 'Legacy Admin Panel Redesign',
          body: 'Led the full redesign and rebuild of a legacy admin panel, migrating from Handlebars, jQuery, and Node.js 8 to a modern full-stack architecture using React, Redux, TypeScript, NestJS, and PostgreSQL. Reduced customer support operation times by 45% through significantly faster page performance, secure routing, and role-based access control via JWT key-pair authentication, backed by a full dependency audit and security hardening.',
        },
        {
          title: 'Apple Pay & Google Pay Tokenisation',
          body: 'Spearheaded the front-end implementation of Apple Pay and Google Pay tokenisation alongside a server-side paginated token management table — with sorting, suspension, unbinding, and deletion capabilities — strengthening transaction security and customer trust in the platform.',
        },
        {
          title: 'Merchant Blocklist Feature',
          body: 'Led front-end and backend development of a merchant blocklist feature enabling parents to block payments to specific merchant categories. Built with React, TypeScript, and Next.js on the frontend and RESTful Node.js endpoints on the backend, with CRUD operations managed via PostgreSQL. Directly supported NatWest\'s safeguarding obligations for child account holders.',
        },
      ],
    },
    onefile: {
      title: 'OneFile',
      intro:
        'Responsible for the frontend development of two key applications: the SSR enrolment web app Enrol, built with React, Next.js, Redux Toolkit, and Styled-Components; and an Angular-based internal admin application. Also contributed to the ASP.NET Core / SQL Server backend with CRUD operations optimised for scalability. The platform was hosted on Microsoft Azure infrastructure.',
      highlights: [
        {
          title: 'Key Features Delivered',
          body: 'Built a dynamic web form builder, PDF export, CSV import, and user login authentication. Developed a reusable Styled-Components design system to ensure UI consistency across the application, alongside a Flask/Python RESTful API for managing apprenticeship policies and government regulations.',
        },
        {
          title: 'Testing & Quality',
          body: 'Maintained comprehensive coverage through unit tests, integration tests, and snapshot tests, collaborating closely with project managers, QA specialists, and designers within an agile Scrum framework.',
        },
        {
          title: 'Training & Knowledge Sharing',
          body: 'Delivered internal training sessions on React hooks and responsive CSS, with a focus on Flexbox and Grid layout techniques.',
        },
      ],
    },
    tickiteline: {
      title: 'Ticketline',
      intro:
        'Led front-end development of a greenfield SSR ticket reseller platform, built with React, Next.js, TypeScript, Redux, and SCSS, with additional backend contributions via ASP.NET Core. The platform was hosted on Microsoft Azure infrastructure.',
      highlights: [
        {
          title: 'Front-End Architecture',
          body: "Assumed primary responsibility for the React SSR application, architecting reusable components including a calendar, event-search with multi-faceted filtering, and server-side paginated results — delivering approximately 60% of the application's functionality with 60% unit test coverage.",
        },
        {
          title: 'Mentoring & Training',
          body: 'Provided internal training sessions on TypeScript, React SSR, HTML, and advanced CSS layout techniques including Flexbox and Grid, helping colleagues broaden their skill sets and elevate their contributions to the project.',
        },
        {
          title: 'Web Scraping Tool',
          body: 'Developed a content scraping tool using Python, JavaScript, HTML, and CSS for the marketing team to track competitor pricing, product updates, and market insights.',
        },
      ],
    },
    sabs: {
      title: 'SABS Travel Technologies',
      intro:
        'Played a key role in developing the Lightning business travel booking platform using Angular 14, TypeScript, RxJS, GraphQL, SCSS, and Storybook, contributing to a seamless and responsive experience for business travellers.',
      highlights: [
        {
          title: 'Flight Seat Map UI',
          body: 'Designed and built an interactive seat map interface enabling users to select seats with detailed attributes such as infant suitability and legroom, streamlining the booking process and improving the overall user experience.',
        },
      ],
    },
    rhinogroup: {
      title: 'Rhino Group',
      intro: '',
      highlights: [
        {
          title: 'Web Scraping Tool',
          body: 'Developed a content scraping tool using Python, JavaScript, HTML, and CSS, used primarily by the marketing team to monitor competitor pricing, product updates, and other key market insights.',
        },
        {
          title: 'Custom CMS Application',
          body: 'Played a significant role in building a custom content management system (CMS) using Angular, consuming a RESTful API built with Laravel connected to a MySQL database. The application streamlined the internal creation of landing pages, providing a user-friendly interface for rapid page production.',
        },
      ],
    },
  },
};
